import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { COURSE_API_URL, CONTACT_API_URL } from './lib/api-config';
import { useAuth } from './contexts/AuthContext';
import './Dashboard.css';

function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeView, setActiveView] = useState(
    () => localStorage.getItem('kursadmin-view') || 'courses'
  );

  // Listen for view changes from App.js header toggle
  useEffect(() => {
    const handleStorage = () => {
      setActiveView(localStorage.getItem('kursadmin-view') || 'courses');
    };
    window.addEventListener('storage', handleStorage);

    // Also poll localStorage since storage event doesn't fire in same tab
    const interval = setInterval(() => {
      const current = localStorage.getItem('kursadmin-view') || 'courses';
      setActiveView(prev => prev !== current ? current : prev);
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  const config = { withCredentials: true };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      if (activeView === 'sales') {
        const response = await axios.get(`${CONTACT_API_URL}/sales-dashboard`, config);
        setDashboardData({ view: 'sales', ...response.data.data });
      } else {
        const response = await axios.get(`${COURSE_API_URL}/dashboard`, config);
        setDashboardData({ view: 'courses', ...response.data.data });
      }
      setError(null);
    } catch (err) {
      console.error('Kunde inte hämta dashboard-data:', err);
      setError('Kunde inte ladda dashboard-data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMonthName = () => {
    return new Date().toLocaleDateString('sv-SE', { month: 'long' });
  };

  const getNextMonthName = () => {
    const next = new Date();
    next.setMonth(next.getMonth() + 1);
    return next.toLocaleDateString('sv-SE', { month: 'long' });
  };

  const getFillRateColor = (rate) => {
    if (rate >= 80) return '#27ae60';
    if (rate >= 50) return '#f39c12';
    return '#e74c3c';
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min sedan`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h sedan`;
    const days = Math.floor(hours / 24);
    return `${days}d sedan`;
  };

  if (loading) {
    return (
      <main className="dashboard-main">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Laddar dashboard...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="dashboard-main">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn-primary">
            Försök igen
          </button>
        </div>
      </main>
    );
  }

  // ========== SALES DASHBOARD ==========
  if (dashboardData?.view === 'sales') {
    const { kpis, closingSoon, recentActivities } = dashboardData;
    return (
      <main className="dashboard-main">
        <div className="dashboard-header">
          <h1>Sälj & Marknad</h1>
          <p className="welcome-text">Välkommen, {user?.firstName || 'Användare'}!</p>
        </div>

        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-icon deals-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
              </svg>
            </div>
            <div className="kpi-content">
              <h3>Aktiva affärer</h3>
              <div className="kpi-value">{kpis?.activeDeals || 0}</div>
              <div className="kpi-subtext">
                Värde: {formatCurrency(kpis?.activeDealsValue || 0)}
              </div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon revenue-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
              </svg>
            </div>
            <div className="kpi-content">
              <h3>Viktat pipeline-värde</h3>
              <div className="kpi-value">{formatCurrency(kpis?.weightedPipelineValue || 0)}</div>
              <div className="kpi-subtext">
                Vunnet i {getMonthName()}: {formatCurrency(kpis?.wonValueThisMonth || 0)}
              </div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon win-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z"/>
              </svg>
            </div>
            <div className="kpi-content">
              <h3>Win rate (90 dagar)</h3>
              <div className="kpi-value">{kpis?.winRate || 0}%</div>
              <div className="kpi-subtext">
                {kpis?.wonThisMonth || 0} vunna, {kpis?.lostThisMonth || 0} förlorade i {getMonthName()}
              </div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon contacts-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
            <div className="kpi-content">
              <h3>Nya kontakter</h3>
              <div className="kpi-value">{kpis?.newContactsThisMonth || 0}</div>
              <div className="kpi-subtext">
                i {getMonthName()}
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-columns">
          <div className="dashboard-section">
            <h2>Affärer som stängs snart</h2>
            {closingSoon && closingSoon.length > 0 ? (
              <div className="closing-soon-list">
                {closingSoon.map(deal => (
                  <div key={deal.id} className="deal-item">
                    <div className="deal-info">
                      <h4>{deal.name}</h4>
                      <p className="deal-company">{deal.company}</p>
                      <div className="deal-meta">
                        <span className="deal-value">{formatCurrency(deal.expectedValue || 0)}</span>
                        <span className="deal-probability">{deal.probability}%</span>
                        <span
                          className="deal-stage-badge"
                          style={{ background: deal.stageColor + '20', color: deal.stageColor }}
                        >
                          {deal.stageName}
                        </span>
                      </div>
                    </div>
                    <div className="deal-close-date">
                      {formatDate(deal.expectedCloseDate)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">Inga affärer med stängningsdatum de närmaste 30 dagarna</p>
            )}
          </div>

          <div className="dashboard-section">
            <h2>Senaste aktiviteter</h2>
            {recentActivities && recentActivities.length > 0 ? (
              <div className="activities-list">
                {recentActivities.map(activity => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon-wrapper">
                      {activity.activityIcon || '📋'}
                    </div>
                    <div className="activity-info">
                      <h4>{activity.subject}</h4>
                      <p className="activity-deal">{activity.dealName}</p>
                      <p className="activity-user">{activity.userName}</p>
                    </div>
                    <div className="activity-time">
                      {timeAgo(activity.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">Inga aktiviteter ännu</p>
            )}
          </div>
        </div>
      </main>
    );
  }

  // ========== COURSES DASHBOARD (default) ==========
  const { kpis, upcomingCourses, recentBookings } = dashboardData || {};

  return (
    <main className="dashboard-main">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="welcome-text">Välkommen, {user?.firstName || 'Användare'}!</p>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon bookings-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Bokningar i {getMonthName()}</h3>
            <div className="kpi-value">{kpis?.bookingsThisMonth || 0}</div>
            {kpis?.bookingsTrend !== undefined && (
              <div className={`kpi-trend ${kpis.bookingsTrend >= 0 ? 'positive' : 'negative'}`}>
                {kpis.bookingsTrend >= 0 ? '+' : ''}{kpis.bookingsTrend}% jmf förra mån
              </div>
            )}
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon revenue-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Intäkter {getMonthName()}</h3>
            <div className="kpi-value">{formatCurrency(kpis?.revenueThisMonth || 0)}</div>
            <div className="kpi-subtext">
              Forecast {getNextMonthName()}: {formatCurrency(kpis?.revenueNextMonth || 0)}
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon total-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Totalt bekräftade</h3>
            <div className="kpi-value">{kpis?.totalBookingsConfirmed || 0}</div>
            <div className="kpi-subtext">
              {kpis?.pendingCompletions || 0} saknar komplettering
            </div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon courses-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
            </svg>
          </div>
          <div className="kpi-content">
            <h3>Aktiva kurser</h3>
            <div className="kpi-value">{kpis?.totalCourses || 0}</div>
            <div className="kpi-subtext">
              Total omsättning: {formatCurrency(kpis?.totalRevenue || 0)}
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-columns">
        {/* Upcoming Courses with Fill Rate */}
        <div className="dashboard-section">
          <h2>Kommande kurser</h2>
          {upcomingCourses && upcomingCourses.length > 0 ? (
            <div className="upcoming-courses-list">
              {upcomingCourses.map((course) => (
                <div key={course.id} className="course-item">
                  <div className="course-info">
                    <h4>{course.courseName}</h4>
                    <p className="course-details">
                      {formatDate(course.startDate)} | {course.location}
                    </p>
                    <p className="course-bookings">
                      {course.confirmedBookings}/{course.maxParticipants} bokade
                      {course.waitlistCount > 0 && (
                        <span className="waitlist-badge">+{course.waitlistCount} väntelista</span>
                      )}
                    </p>
                  </div>
                  <div className="fill-rate-container">
                    <div
                      className="fill-rate-bar"
                      style={{
                        width: `${Math.min(course.fillRate, 100)}%`,
                        backgroundColor: getFillRateColor(course.fillRate)
                      }}
                    ></div>
                    <span className="fill-rate-text">{course.fillRate}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">Inga kommande kurser</p>
          )}
        </div>

        {/* Recent Bookings */}
        <div className="dashboard-section">
          <h2>Senaste bokningar</h2>
          {recentBookings && recentBookings.length > 0 ? (
            <div className="recent-bookings-list">
              {recentBookings.map((booking) => (
                <div key={booking.id} className="booking-item">
                  <div className="booking-info">
                    <h4>{booking.participantName}</h4>
                    <p className="booking-company">{booking.company || '-'}</p>
                    <p className="booking-course">{booking.courseName}</p>
                  </div>
                  <div className="booking-date">
                    {formatDate(booking.bookingDate)}
                    <span className={`status-badge ${booking.status.toLowerCase()}`}>
                      {booking.status === 'Confirmed' ? 'Bekräftad' : booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">Inga senaste bokningar</p>
          )}
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
